import type { JSX } from "react";
import { CirclePlus } from "lucide-react";
import { istNurLeser } from "../../state/nurLeser.ts";
import type { Nutzer } from "../../state/reduzierer.ts";
import { Flagge } from "../Flagge.tsx";
import { Ikone } from "../Ikone.tsx";
import type { LaenderFilter, TransportartFilter } from "../../state/arbeitsplatz.ts";
import { MarkenMenue } from "./MarkenMenue.tsx";
import {
  AKTIVITAETEN_SEITE,
  DASHBOARD_SEITE,
  KI_ERLAUBEN_SEITE,
  KI_ZUGAENGE_SEITE,
  LIEFERANTEN_SEITE,
  LIQUIDITAET_SEITE,
  Navigation,
  ZAHLUNGSVORLAGEN_SEITE,
  type AktiveSeite,
} from "./Navigation.tsx";

export interface KopfzeileProps {
  /**
   * Task 11: `AktiveSeite` statt `Seite` - die Kopfzeile muss auch die Lieferantenseite als aktive
   * Seite tragen koennen. `Seite` selbst bleibt unangetastet (siehe `Navigation.tsx`, Kommentar an
   * `AktiveSeite`: `state/berechtigungen.ts` wird wortgleich gegen `index.html` gehalten). Da
   * `Seite` ein Teil der Union ist, bleibt jeder bisherige Aufrufer typkorrekt.
   *
   * FIX-RUNDE 1 (2026-08-11): sie traegt dabei KEINEN Reiter mehr - die Flaeche zog ins Marken-Menue
   * (`MarkenMenue.tsx`), genau wie `users`. `<Navigation>` markiert bei `seite === "suppliers"`
   * folglich keinen der fuenf Reiter, und das ist der gewollte Zustand.
   */
  seite: AktiveSeite;
  rechte: ReadonlySet<string>;
  /**
   * Erweitert gegenüber der Skizze aus Task-Brief, Schritt 1 (`{ displayName, role }`) um
   * `username`: `#brandAccountRole` (index.html:5372) zeigt `${username} · ${roleLabel}`, nicht die
   * Rollen-ID zweimal. Ohne `username` gäbe es dafür keinen Ersatz außer der Rollen-ID selbst - für
   * das Bootstrap-Konto (`admin`/`admin`) unauffällig identisch, für jedes andere Konto sichtbar
   * falsch. `kopfzeile.test.tsx` bleibt unberührt: es übergibt `nutzer` nie ungleich `null`, die
   * Erweiterung ist für den vorgegebenen Test unsichtbar.
   */
  nutzer: { displayName: string; username: string; role: string } | null;
  onNeueBestellung: () => void;
  /**
   * Task 10 (Marken-Menue und Nutzerpfad), seit Fix-Runde 1 zu Task 11 auch `onLieferanten`, seit
   * dem 2026-08-12 auch `onTaskTemplate`: reine Durchreichung an `<MarkenMenue>` - siehe dort fuer
   * die Herkunft jedes einzelnen Rueckrufs. Die historischen fuenf Verwaltungsrueckrufe bleiben
   * optional, weil alte Tests mit `nutzer={null}` das Marken-Menue nie rendern. `onUsdKonten` ist
   * dagegen Pflicht: mit `manage_fx` ist der sichtbare Verwaltungszugang vorhanden und darf nicht
   * durch eine vergessene Verdrahtung still wirkungslos werden. `App.tsx` uebergibt alle sechs.
   */
  onPasswort?: () => void;
  onAbmelden?: () => void;
  onNutzerverwaltung?: (id: string | null) => void;
  onLieferanten?: () => void;
  /** Durchreichung an `<MarkenMenue>`; `App.tsx` uebergibt sie immer. */
  onZahlungsvorlagen?: () => void;
  /** Durchreichung an `<MarkenMenue>` ("Meine KI-Zugänge"); `App.tsx` uebergibt sie immer. */
  onKiZugaenge?: () => void;
  /** Durchreichung an `<MarkenMenue>`: der Eintrag "Meine KI-Zugänge" steht nur bei `true`; weggelassen = `false`. */
  kiZugangAktiv?: boolean;
  /** Pflicht, weil `manage_fx` einen sichtbaren und wirksamen Verwaltungszugang freischaltet. */
  onUsdKonten: () => void;
  onTaskTemplate?: () => void;
  nutzerLaden?: () => Promise<Nutzer[]>;
  /**
   * Laenderfilter (Auftrag „EU/China-Laenderfilter in die App-Kopfzeile verschieben", 2026-08-27):
   * bis hierher lokaler Zustand von `Board/BoardSeite.tsx`, Bedienung in `Board/Werkzeugleiste.tsx`
   * (dort NEUBAU ohne Bestandsvorbild) - seither app-weiter Zustand in `App.tsx`
   * (`state/arbeitsplatz.ts#leseLaenderFilter`/`schreibeLaenderFilter`), die Chips ziehen hierher,
   * neben "Neue Bestellung". Siehe Kopfkommentar am `<header>` unten, Abschnitt "Vierter Kindknoten".
   */
  laenderFilter: LaenderFilter;
  /** Kippt genau EINEN der beiden Chips - der Aufrufer persistiert (`state/arbeitsplatz.ts`). */
  onLaenderFilterAendern: (naechster: LaenderFilter) => void;
  /**
   * Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub", 2026-08-27) -
   * dieselbe Bauart wie `laenderFilter` oben, zwei Chips LINKS von EU/China (siehe Kopfkommentar am
   * `<header>` unten, Abschnitt „Vierter Kindknoten"). Zustand app-weit in `App.tsx`
   * (`state/arbeitsplatz.ts#leseTransportartFilter`/`schreibeTransportartFilter`).
   */
  transportartFilter: TransportartFilter;
  /** Kippt genau EINEN der beiden Chips - der Aufrufer persistiert (`state/arbeitsplatz.ts`). */
  onTransportartFilterAendern: (naechster: TransportartFilter) => void;
}

/** index.html:3670 nutzt &nbsp; zwischen "Tracking" und "Hub" - hier als Unicode-Escape, damit der
 * Zeilenumbruch beim schmaleren Viewport (hell-1440) nicht an anderer Stelle erfolgt als im
 * Original. */
const APP_TITEL = "Bestell-Tracking Hub";

/**
 * `<header>` (index.html:3635-3679). Die Reihenfolge der Kinder ist tragend: die Reitergruppe steht
 * per `position: absolute; left: 50%` zentriert, "Neue Bestellung" hängt über `margin-left: auto`
 * rechts (Task-Brief, Schritt 4) - beides setzt voraus, dass `.brand-access`, `.app-title`, `nav`
 * und `.btn-new` in genau dieser Reihenfolge direkte Geschwister im selben `<header>` sind.
 *
 * "Neue Bestellung" wird unabhängig von einzelnen Rechten gerendert: das Original blendet den Button
 * zusätzlich nach `edit_orders` aus (`updateUserUi`, index.html:5389), aber der vorgegebene
 * Komponententest (Task-Brief, Schritt 2) rendert mit einer Rechtemenge OHNE `edit_orders` und
 * erwartet den Button trotzdem in der Liste der Buttons - dieser Fund wird hier gemeldet,
 * nicht repariert (Wörtlichkeitsregel).
 *
 * AUSNAHME Kontotyp "Nur lesen" (Task 6 des Plans „Kontotyp Nur lesen", Neubau ohne Bestandsvorbild):
 * ein Konto ohne jedes Schreibrecht (`state/nurLeser.ts#istNurLeser`, auch das noch leere Rechte-Set vor
 * dem Laden von `/api/auth/me`) bekommt den Knopf nicht - "Neue Bestellung" legt an, und der Schreibriegel
 * des Servers lehnt das mit „Dein Konto hat nur Leserechte." ab. Jedes Konto mit EINEM Schreibrecht (auch
 * `payment_manager` ohne `edit_orders`) sieht ihn unverändert. Reine Aktion, deshalb ausgeblendet statt
 * gesperrt; der Wrapper `.kopfzeile-aktionen` bleibt (er trägt die Filter-Chips).
 *
 * TASK 11, FIX-RUNDE 1 (Entscheidung des Auftraggebers, 2026-08-11): die Lieferantenfläche hing
 * kurzzeitig als SECHSTER Reiter in der Reitergruppe. Sie ist ins Marken-Menü umgezogen
 * (`MarkenMenue.tsx`) - die Reitergruppe führt damit wieder genau die fünf Bestandsreiter, die
 * Knopfzählung dieser Kopfzeile steht wieder bei sechs, und die Kopfzeile ist wieder pixelgleich zum
 * Bestand. Die Reihenfolge der vier `<header>`-Kinder war und bleibt unberührt.
 *
 * ================= Vierter Kindknoten: `.kopfzeile-aktionen` (2026-08-27) ======================
 *
 * Die beiden EU/China-Chips (bis hierher `Board/Werkzeugleiste.tsx`, Zustand in `BoardSeite.tsx`,
 * siehe deren Kopfkommentar „Laenderfilter") ziehen app-weit hierher, sichtbar neben "Neue
 * Bestellung" - Auftrag „EU/China-Laenderfilter in die App-Kopfzeile verschieben". Ein FUENFTES
 * `<header>`-Kind haette den Absatz oben Luegen gestraft (`nav`s `left: 50%` bliebe zwar unberuehrt,
 * aber `.btn-new`s `margin-left: auto`, in `bestand.css` UNVERAENDERT an `.btn-new` selbst
 * haengend, kennt kein neues Geschwister rechts von sich).
 *
 * Stattdessen: Chips und Knopf stecken gemeinsam in `.kopfzeile-aktionen`
 * (`styles/neubau/laenderfilter.css`, NEUE Regel, NICHT `bestand.css`), die selbst die
 * `margin-left: auto`-Rolle uebernimmt - der `<header>` fuehrt damit weiterhin GENAU VIER direkte
 * Kinder, `.brand-access`/`.app-title`/`nav` bleiben unangetastete Geschwister. `.btn-new`s eigene
 * `margin-left: auto` (bestand.css, woertlich unveraendert) wird dadurch NICHT falsch: sie greift
 * innerhalb von `.kopfzeile-aktionen`, das exakt so breit ist wie sein Inhalt (kein `flex-grow`) -
 * ohne freien Raum darin loest ein Auto-Rand zu 0px auf, der Knopf haengt also weiterhin ganz
 * rechts, jetzt nur zusaetzlich hinter den beiden Chips statt direkt hinter `nav`.
 *
 * Damit ist diese Flaeche eine bewusste, dauerhafte Abweichung von der Kopfzeilen-Baseline
 * (`test/visual/seiten/kopfzeile.spec.ts`, `kopfzeile-hell`/`kopfzeile-dunkel`) - siehe
 * `test/visual/ausnahmen.ts`, Eintrag „App/Kopfzeile".
 *
 * NACHTRAG (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub", 2026-08-27): dieselbe Herleitung
 * gilt seither fuer ein ZWEITES Chip-Paar. `.kopfzeile-aktionen` fuehrt seitdem NICHT zwei, sondern
 * VIER Chips plus "Neue Bestellung" - der `<header>` bleibt trotzdem bei GENAU VIER direkten
 * Kindern, aus demselben Grund wie oben (kein fuenftes/sechstes Geschwister, `.btn-new`s
 * `margin-left: auto` bleibt unveraendert gueltig).
 *
 * ================= Befund G3 (Pruefrunde 2): kein Bestellbezug auf zwei Seiten ===================
 *
 * Die Chips wirken auf `#page-users` (Nutzerverwaltung) und `#page-suppliers` (Lieferantenseite)
 * nicht - dort gibt es keine Bestellliste, die Laender- oder Transportart-Filter einschraenken
 * koennten. Statt sie dort wirkungslos mitzuzeigen, blendet `Kopfzeile` unten nur die CHIPS aus
 * (nicht den ganzen `.kopfzeile-aktionen`-Wrapper - "Neue Bestellung" bleibt auf jeder Seite
 * bedienbar). Das ruehrt an KEINER Klasse aus `styles/neubau/laenderfilter.css` und aendert die
 * Kindknotenfolge des `<header>` nicht - reine Inhaltsentscheidung INNERHALB von
 * `.kopfzeile-aktionen`, kollisionsfrei mit dem parallelen Zweig
 * `fix/laenderfilter-kopfzeile-nav-kollision` (der dort nur die CSS-Breite der Reitergruppe
 * anpasst). GILT SEIT DEM 2026-08-27 FUER BEIDE Chip-Gruppen gleichermassen - beide pruefen dieselbe
 * lokale Konstante `zeigeBestellbezogeneChips` unten (Nachbesserung zum Transportart-Filter, nach
 * Commit c9da74e: bis dahin stand die Bedingung wortgleich zweimal da).
 */
export function Kopfzeile({
  seite,
  rechte,
  nutzer,
  onNeueBestellung,
  onPasswort = () => {},
  onAbmelden = () => {},
  onNutzerverwaltung = () => {},
  onLieferanten = () => {},
  onZahlungsvorlagen = () => {},
  onKiZugaenge = () => {},
  kiZugangAktiv = false,
  onUsdKonten,
  onTaskTemplate = () => {},
  nutzerLaden,
  laenderFilter,
  onLaenderFilterAendern,
  transportartFilter,
  onTransportartFilterAendern,
}: KopfzeileProps): JSX.Element {
  // Befund G3 (siehe Kopfkommentar oben): auf Nutzerverwaltung und Lieferantenseite gibt es keine
  // Bestellliste, die ein Filter einschraenken koennnte - beide Chip-Gruppen pruefen deshalb
  // dieselbe Bedingung. Nachbesserung zum Transportart-Filter (nach Commit c9da74e, Punkt 6d): bis
  // dahin stand `seite !== "users" && seite !== LIEFERANTEN_SEITE` wortgleich zweimal im JSX unten.
  // Liquiditaetsplanung: eigene Sicht auf alle USD-Raten, die Chips wuerden dort nichts filtern
  // (die Seite wendet sie bewusst nicht an) - ein unwirksamer Filter waere eine Falle.
  // Zahlungen: dasselbe - die Seite zeigt Konten, Sicherungen und alle Bestellungen ungefiltert.
  // Aktivitaeten: das Protokoll aller Aenderungen kennt keinen Laender- oder Transportartfilter.
  // Dashboard: die Seite zeigt die Anlieferungen aus China ueber alle Transportarten; die Chips wirkten dort nicht.
  // KI-Zugang (Erlauben-Seite, Meine KI-Zugaenge): zeigt Verbindungen, keine Bestellungen.
  const zeigeBestellbezogeneChips =
    seite !== "users" && seite !== LIEFERANTEN_SEITE && seite !== LIQUIDITAET_SEITE &&
    seite !== "payments" && seite !== ZAHLUNGSVORLAGEN_SEITE && seite !== AKTIVITAETEN_SEITE &&
    seite !== DASHBOARD_SEITE && seite !== KI_ERLAUBEN_SEITE && seite !== KI_ZUGAENGE_SEITE;
  // Kontotyp "Nur lesen": siehe Kopfkommentar, "AUSNAHME".
  const nurLeser = istNurLeser(rechte);
  return (
    <header>
      <MarkenMenue
        nutzer={nutzer}
        rechte={rechte}
        onPasswort={onPasswort}
        onAbmelden={onAbmelden}
        onNutzerverwaltung={onNutzerverwaltung}
        onLieferanten={onLieferanten}
        onZahlungsvorlagen={onZahlungsvorlagen}
        onKiZugaenge={onKiZugaenge}
        kiZugangAktiv={kiZugangAktiv}
        onUsdKonten={onUsdKonten}
        onTaskTemplate={onTaskTemplate}
        nutzerLaden={nutzerLaden}
      />
      <div className="app-title">{APP_TITEL}</div>
      <Navigation seite={seite} rechte={rechte} />
      <div className="kopfzeile-aktionen">
        {/* Befund G3: kein Bestellbezug auf Nutzerverwaltung und Lieferantenseite - siehe
            Kopfkommentar oben, „Befund G3". Gilt fuer BEIDE Filter (Transportart und Laender)
            gleichermassen. */}
        {zeigeBestellbezogeneChips ? (
          <div className="kopfzeile-transportart-filter">
            <button
              type="button"
              className="kopfzeile-transportart-chip"
              aria-pressed={transportartFilter.see}
              title={
                transportartFilter.see ? "Seefracht-Bestellungen ausblenden" : "Seefracht-Bestellungen einblenden"
              }
              aria-label={
                transportartFilter.see ? "Seefracht-Bestellungen ausblenden" : "Seefracht-Bestellungen einblenden"
              }
              onClick={() =>
                onTransportartFilterAendern({ ...transportartFilter, see: !transportartFilter.see })
              }
            >
              <Ikone name="ship" /> <span className="kopfzeile-chip-label">See</span>
            </button>
            <button
              type="button"
              className="kopfzeile-transportart-chip"
              aria-pressed={transportartFilter.air}
              title={
                transportartFilter.air ? "Luftfracht-Bestellungen ausblenden" : "Luftfracht-Bestellungen einblenden"
              }
              aria-label={
                transportartFilter.air ? "Luftfracht-Bestellungen ausblenden" : "Luftfracht-Bestellungen einblenden"
              }
              onClick={() =>
                onTransportartFilterAendern({ ...transportartFilter, air: !transportartFilter.air })
              }
            >
              <Ikone name="plane" /> <span className="kopfzeile-chip-label">Flug</span>
            </button>
            <button
              type="button"
              className="kopfzeile-transportart-chip"
              aria-pressed={transportartFilter.road}
              title={
                transportartFilter.road
                  ? "Straßenfracht-Bestellungen ausblenden"
                  : "Straßenfracht-Bestellungen einblenden"
              }
              aria-label={
                transportartFilter.road
                  ? "Straßenfracht-Bestellungen ausblenden"
                  : "Straßenfracht-Bestellungen einblenden"
              }
              onClick={() =>
                onTransportartFilterAendern({ ...transportartFilter, road: !transportartFilter.road })
              }
            >
              <Ikone name="truck" /> <span className="kopfzeile-chip-label">Straße</span>
            </button>
          </div>
        ) : null}
        {zeigeBestellbezogeneChips ? (
          <div className="kopfzeile-laender-filter">
            <button
              type="button"
              className="kopfzeile-laender-chip"
              aria-pressed={laenderFilter.eu}
              title={laenderFilter.eu ? "EU-Lieferanten ausblenden" : "EU-Lieferanten einblenden"}
              aria-label={laenderFilter.eu ? "EU-Lieferanten ausblenden" : "EU-Lieferanten einblenden"}
              onClick={() => onLaenderFilterAendern({ ...laenderFilter, eu: !laenderFilter.eu })}
            >
              <Flagge region="eu" aria-hidden="true" /> <span className="kopfzeile-chip-label">EU</span>
            </button>
            <button
              type="button"
              className="kopfzeile-laender-chip"
              aria-pressed={laenderFilter.china}
              title={
                laenderFilter.china ? "China-Lieferanten ausblenden" : "China-Lieferanten einblenden"
              }
              aria-label={
                laenderFilter.china ? "China-Lieferanten ausblenden" : "China-Lieferanten einblenden"
              }
              onClick={() => onLaenderFilterAendern({ ...laenderFilter, china: !laenderFilter.china })}
            >
              <Flagge region="china" aria-hidden="true" /> <span className="kopfzeile-chip-label">China</span>
            </button>
          </div>
        ) : null}
        {nurLeser ? null : (
          <button className="btn-new" id="newOrderButton" onClick={onNeueBestellung}>
            <CirclePlus aria-hidden="true" strokeWidth={1.8} />
            Neue Bestellung
          </button>
        )}
      </div>
    </header>
  );
}
